import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { StrategyGuidanceBanner } from '../strategy/StrategyGuidanceBanner';
import { BuildingRow } from './BuildingRow';
import { LandDistributionBar } from './LandDistributionBar';
import { RazePanel } from './RazePanel';
import { BuildingOverviewPanel } from './BuildingOverviewPanel';
import { COLORS } from '../../constants/theme';
import { BUILDING_PAIRS, BUILDING_MAP } from '../../constants/buildings';
import { BUILDINGS_TABS } from '../../constants/buildingsTabs';
import { formatNumber } from '../../utils/format';
import type { BuildingsShellProps } from '../../pages/Buildings';

const ROW_HOVER_STYLE = `
  .building-row-highlight:hover {
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

/**
 * SF-132: thin wrapper around the existing (already desktop-appropriate, SF-69) Buildings JSX —
 * no layout redesign. Adds a real, focus-managed `<h1>` (matching DesktopThroneShell's
 * treatment) — the only change beyond receiving props instead of local state/hooks.
 */
export function DesktopBuildingsShell({
  activeStrategy,
  strategyLoading,
  activeTab,
  setActiveTab,
  buildQty,
  isAccelerated,
  setIsAccelerated,
  isSubmitting,
  submitError,
  successMessage,
  highlighted,
  handleBuildQtyChange,
  handleFillDeficit,
  handleOptimizeAll,
  handleClearAll,
  handleSubmitClick,
  totalLand,
  barren,
  buildings,
  inProgress,
  totalBuildings,
  totalBuildQty,
  remainingBarren,
  constructionTime,
  costPerAcre,
  totalCost,
  be,
  province,
  worldId,
  provinceId,
  refresh,
}: BuildingsShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const builtTotal = totalBuildings;

  const buildableCard = {
    label: 'Buildable Acres',
    value: `${formatNumber(remainingBarren)} acres`,
    sub: `of ${formatNumber(barren)} barren`,
  };

  const buildTimeCard = {
    label: 'Build Time',
    value: `${constructionTime} days`,
    sub: isAccelerated ? 'Accelerated' : 'Normal',
  };

  const barrenCard = {
    label: 'Barren Land',
    value: `${formatNumber(barren)} acres`,
    sub: `of ${formatNumber(totalLand)} total`,
  };

  const freeCreditsCard = {
    label: 'Free Credits',
    value: '0',
    sub: 'From conquests',
  };

  const costCard = {
    label: 'Cost per Acre',
    value: `${formatNumber(costPerAcre)} gc`,
    sub: isAccelerated ? '2× accelerated' : 'Normal rate',
  };

  return (
    <div className="space-y-4">
      <style>{ROW_HOVER_STYLE}</style>

      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Buildings
      </h1>

      <StrategyGuidanceBanner activeStrategy={activeStrategy} isLoading={strategyLoading} page="growth" />

      <div className="flex border-b" style={{ borderColor: COLORS.border }} role="tablist">
        {BUILDINGS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              id={`buildings-tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`buildings-panel-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'build' && (
        <div role="tabpanel" id="buildings-panel-build" aria-labelledby="buildings-tab-build" className="space-y-4">
          {/* Stats Row */}
          <div className="flex gap-3">
            <div className="w-36"><StatCard {...buildableCard} borderless /></div>
            <div className="w-36"><StatCard {...barrenCard} borderless /></div>
            <div className="w-36"><StatCard {...freeCreditsCard} borderless /></div>
            <div className="w-36"><StatCard {...buildTimeCard} borderless /></div>
            <div className="w-36"><StatCard {...costCard} borderless /></div>
          </div>

          {/* Land Distribution Bar */}
          <div className="px-4">
            <LandDistributionBar
              totalLand={totalLand}
              builtAcres={builtTotal}
              pendingAcres={totalBuildQty}
              barrenAcres={Math.max(0, remainingBarren)}
            />
          </div>

          {/* Building List (two-column) */}
          <div
            className="rounded overflow-hidden"
            style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
          >
            {/* Column Headers */}
            <div className="grid grid-cols-2" style={{ backgroundColor: COLORS.sectionHeader }}>
              {[0, 1].map((col) => (
                <div
                  key={col}
                  className="grid items-center px-3 py-1.5"
                  style={{
                    gridTemplateColumns: '1fr 64px 64px 88px',
                    borderLeft: col === 1 ? `1px solid ${COLORS.border}` : undefined,
                  }}
                >
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>Building</span>
                  <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>HAVE</span>
                  <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>IN PROG</span>
                  <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>BUILD</span>
                </div>
              ))}
            </div>
            {/* Building Pairs */}
            {BUILDING_PAIRS.map(([leftId, rightId]) => {
              const renderCell = (id: string) => {
                const building = BUILDING_MAP[id];
                if (!building) return null;
                const owned = buildings[building.id] ?? 0;
                const inProg = inProgress[building.id] ?? 0;
                const targetPct = activeStrategy?.settings?.buildings[building.id] ?? null;
                const qty = buildQty[building.id] ?? 0;
                const otherTotal = totalBuildQty - qty;
                const rowRemainingBarren = barren - otherTotal;

                return (
                  <BuildingRow
                    key={building.id}
                    buildingId={building.id}
                    name={building.name}
                    owned={owned}
                    inProgress={inProg}
                    totalLand={totalLand}
                    totalBuildings={totalBuildings}
                    be={be}
                    targetPct={targetPct !== undefined ? targetPct : null}
                    buildQty={qty}
                    remainingBarren={Math.max(0, rowRemainingBarren)}
                    highlighted={highlighted[building.id] ?? false}
                    onBuildQtyChange={handleBuildQtyChange}
                    onFillDeficit={handleFillDeficit}
                  />
                );
              };

              return (
                <div key={`${leftId}-${rightId}`} className="grid grid-cols-2" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <div>{renderCell(leftId)}</div>
                  <div style={{ borderLeft: `1px solid ${COLORS.border}` }}>{renderCell(rightId)}</div>
                </div>
              );
            })}

            {/* Submit Bar */}
            <div
              className="px-4 py-3 space-y-3"
              style={{ borderTop: `1px solid ${COLORS.border}` }}
            >
              {/* Messages */}
              {submitError && (
                <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
              )}
              {successMessage && (
                <div className="text-sm" style={{ color: COLORS.positive }}>{successMessage}</div>
              )}

              {/* Summary row */}
              <div className="flex items-center gap-6 text-sm">
                <span className="whitespace-nowrap">
                  <span style={{ color: COLORS.text.tertiary }}>Building: </span>
                  <span
                    className="font-mono"
                    style={{ color: totalBuildQty > barren ? COLORS.negative : COLORS.text.primary }}
                  >
                    {formatNumber(totalBuildQty)} acres
                  </span>
                </span>
                <span className="whitespace-nowrap">
                  <span style={{ color: COLORS.text.tertiary }}>Construction Cost: </span>
                  <span className="font-mono" style={{ color: totalCost > 0 ? COLORS.gold : COLORS.text.tertiary }}>
                    {formatNumber(totalCost)} gc
                  </span>
                </span>
              </div>

              {/* Actions row */}
              <div className="flex items-center gap-4">
                <button
                  onClick={handleOptimizeAll}
                  disabled={!activeStrategy?.settings}
                  className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded"
                  style={{
                    backgroundColor: activeStrategy?.settings ? COLORS.sectionHeader : 'transparent',
                    border: `1px solid ${COLORS.border}`,
                    color: activeStrategy?.settings ? '#B89878' : COLORS.text.tertiary,
                    cursor: activeStrategy?.settings ? 'pointer' : 'default',
                  }}
                >
                  ⚡ Fill Strategy
                </button>

                <label
                  className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
                  style={{ color: COLORS.text.secondary }}
                >
                  <input
                    type="checkbox"
                    checked={isAccelerated}
                    onChange={(e) => setIsAccelerated(e.target.checked)}
                    className="w-4 h-4 rounded"
                  />
                  Accelerated (2× cost, ½ time)
                </label>

                <div className="flex gap-2 ml-auto">
                  <Button variant="secondary" onClick={handleClearAll} disabled={totalBuildQty === 0}>
                    Clear All
                  </Button>
                  <Button
                    onClick={handleSubmitClick}
                    isLoading={isSubmitting}
                    disabled={totalBuildQty === 0}
                  >
                    Construct Buildings
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'raze' && (
        <div role="tabpanel" id="buildings-panel-raze" aria-labelledby="buildings-tab-raze">
          <RazePanel
            province={province}
            activeStrategy={activeStrategy}
            onRazeSuccess={refresh}
            worldId={worldId ?? ''}
            provinceId={provinceId ?? ''}
          />
        </div>
      )}

      {activeTab === 'overview' && (
        <div role="tabpanel" id="buildings-panel-overview" aria-labelledby="buildings-tab-overview">
          <BuildingOverviewPanel province={province} />
        </div>
      )}
    </div>
  );
}
